import { RouteContentSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import React, { useEffect } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useActiveInstallationContext } from 'src/Libs/installations/context/active-installation-context'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { useTypedSelector } from 'src/State/RootReducer'
import { setActiveInstallation, setActiveStorage } from 'src/State/Storage/StorageActions'
import { resolveActiveInstallationSync } from '../utils'
import { useInstallationAndStorageMismatchStatus } from '../hooks/use-block-navigation-for-installation-and-storage-id-mismatch'

interface Props {
  children: React.ReactElement
}

const notFoundRouteState = {
  titleKey: 'errors.pageNotFound.mismatch-installation-storage.title',
  explanationKey: 'errors.pageNotFound.mismatch-installation-storage.explanation',
  hasAction: false,
}

const InstallationRouteGuard: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch()
  const { installationId, storageId } = useParams<{ installationId?: string; storageId?: string }>()
  const routeInstallationId = installationId ? Number.parseInt(installationId) : undefined
  const routeStorageId = storageId && storageId !== 'undefined' && storageId !== 'null' ? storageId : undefined
  const { activeInstallation, activeStorage } = useTypedSelector(state => state.storage)
  const installationContext = useActiveInstallationContext()
  const { isChecking: isMismatchCheckPending, isMismatch } = useInstallationAndStorageMismatchStatus()

  useEffect(() => {
    const { installationToActivate, storageToActivate } = resolveActiveInstallationSync({
      routeInstallationId,
      routeStorageId,
      activeInstallation,
      activeStorage,
    })

    if (installationToActivate) {
      dispatch(setActiveInstallation(installationToActivate.installationId, installationToActivate.storageId))
    }

    if (storageToActivate !== undefined) {
      dispatch(setActiveStorage(storageToActivate))
    }
  }, [activeInstallation, activeStorage, dispatch, routeInstallationId, routeStorageId])

  if (!routeInstallationId) return children

  const isRouteInstallationActive = activeInstallation === routeInstallationId
  const isRouteStorageActive = !routeStorageId || activeStorage === routeStorageId
  const isRouteSynced = isRouteInstallationActive && isRouteStorageActive
  const isInstallationResolved = installationContext?.installation?.id === routeInstallationId || Boolean(installationContext?.installationError)

  if (!isRouteSynced || installationContext?.installationLoading || !isInstallationResolved) {
    return <RouteContentSkeleton />
  }

  if (installationContext?.installationErrorStatus === StatusCodes.FORBIDDEN) {
    return <Navigate to="/not-found" state={notFoundRouteState} replace />
  }

  if (routeStorageId && isMismatchCheckPending) {
    return <RouteContentSkeleton />
  }

  if (routeStorageId && isMismatch) {
    return <Navigate to="/not-found" state={notFoundRouteState} replace />
  }

  return children
}

export default InstallationRouteGuard
